import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSave, FaTimes, FaCopy, FaHistory } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import './LegalDocumentsManagement.css';

const LegalDocumentsManagement = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [formData, setFormData] = useState({
    document_type: 'privacy_policy',
    title: '',
    url_slug: '',
    content: '',
    version: '1.0',
    status: 'draft'
  });

  // React Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'color', 'background',
    'align', 'code-block'
  ];

  useEffect(() => {
    console.log('👤 User:', user);
    console.log('🔑 Token in localStorage:', localStorage.getItem('token') ? 'Present' : 'Missing');
    fetchDocuments();
  }, [user]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching legal documents...');
      console.log('🔑 Token:', token ? 'Present' : 'Missing');
      console.log('👤 User authenticated:', !!user);
      console.log('🌐 API URL:', API_ENDPOINTS.LEGAL_DOCUMENTS);
      
      if (!token || !user) {
        console.error('❌ No token or user found');
        setError('Authentication required');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(API_ENDPOINTS.LEGAL_DOCUMENTS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Response received:', response.data);
      setDocuments(response.data.data);
      setError(null);
    } catch (err) {
      console.error('❌ Failed to fetch legal documents:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      setError(err.response?.data?.error || 'Failed to fetch legal documents');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const generateUrlSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      url_slug: generateUrlSlug(title)
    }));
  };

  const resetForm = () => {
    setFormData({
      document_type: 'privacy_policy',
      title: '',
      url_slug: '',
      content: '',
      version: '1.0',
      status: 'draft'
    });
    setEditingDocument(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingDocument) {
        // Update existing document
        await axios.put(API_ENDPOINTS.LEGAL_DOCUMENT_BY_ID(editingDocument.id), formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        // Create new document
        await axios.post(API_ENDPOINTS.LEGAL_DOCUMENTS, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      resetForm();
      fetchDocuments();
    } catch (err) {
      console.error('Failed to save legal document:', err);
      setError(err.response?.data?.error || 'Failed to save legal document');
    }
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    setFormData({
      document_type: document.document_type,
      title: document.title,
      url_slug: document.url_slug,
      content: document.content,
      version: document.version,
      status: document.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(API_ENDPOINTS.LEGAL_DOCUMENT_BY_ID(id), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchDocuments();
    } catch (err) {
      console.error('Failed to delete legal document:', err);
      setError(err.response?.data?.error || 'Failed to delete legal document');
    }
  };

  const handlePublish = async (id) => {
    try {
      await axios.patch(API_ENDPOINTS.PUBLISH_LEGAL_DOCUMENT(id), {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchDocuments();
    } catch (err) {
      console.error('Failed to publish legal document:', err);
      setError(err.response?.data?.error || 'Failed to publish legal document');
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.patch(API_ENDPOINTS.ARCHIVE_LEGAL_DOCUMENT(id), {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchDocuments();
    } catch (err) {
      console.error('Failed to archive legal document:', err);
      setError(err.response?.data?.error || 'Failed to archive legal document');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6b7280', bg: '#f3f4f6' },
      published: { color: '#059669', bg: '#d1fae5' },
      archived: { color: '#dc2626', bg: '#fee2e2' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDocumentTypeLabel = (type) => {
    return type === 'privacy_policy' ? 'Privacy Policy' : 'Terms of Service';
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">

      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Legal Documents Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <FaPlus /> Add New Document
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Form Section */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {editingDocument ? 'Edit Legal Document' : 'Add New Legal Document'}
            </h5>
            <button type="button" className="btn-close" onClick={resetForm}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Document Type *</label>
                                             <select
                           name="document_type"
                           className="form-select"
                           value={formData.document_type}
                           onChange={handleInputChange}
                           required
                         >
                           <option value="privacy_policy">Privacy Policy</option>
                           <option value="terms_of_service">Terms and Conditions</option>
                         </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="Enter document title"
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">URL Slug *</label>
                <input
                  type="text"
                  name="url_slug"
                  className="form-control"
                  value={formData.url_slug}
                  onChange={handleInputChange}
                  placeholder="Enter URL slug"
                  required
                />
                <small className="form-text text-muted">
                  This will be used in the URL: /legal/{formData.url_slug}
                </small>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Version</label>
                <input
                  type="text"
                  name="version"
                  className="form-control"
                  value={formData.version}
                  onChange={handleInputChange}
                  placeholder="e.g., 1.0, 1.1"
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Content *</label>
                <div style={{ border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                  <ReactQuill
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: '300px' }}
                    placeholder="Enter document content..."
                  />
                </div>
              </div>
              
              <div className="form-action-buttons">
                <button type="button" className="form-action-btn btn-secondary" onClick={resetForm}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="form-action-btn btn-primary">
                  <FaSave /> {editingDocument ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="row">
        {documents.map((document) => (
          <div key={document.id} className="col-md-6 mb-4">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">{getDocumentTypeLabel(document.document_type)}</h6>
                  <small className="text-muted">v{document.version}</small>
                </div>
                {getStatusBadge(document.status)}
              </div>
              
              <div className="card-body">
                <h5 className="card-title">{document.title}</h5>
                <p className="card-text text-muted">
                  <strong>URL:</strong> /legal/{document.url_slug}
                </p>
                <p className="card-text">
                  <strong>Created:</strong> {new Date(document.created_at).toLocaleDateString()}
                </p>
                {document.published_at && (
                  <p className="card-text">
                    <strong>Published:</strong> {new Date(document.published_at).toLocaleDateString()}
                  </p>
                )}
                <p className="card-text">
                  <strong>Last Updated:</strong> {new Date(document.updated_at).toLocaleDateString()}
                </p>
                {document.created_by_name && (
                  <p className="card-text">
                    <strong>Created By:</strong> {document.created_by_name}
                  </p>
                )}
              </div>
              
              <div className="card-footer">
                <div className="btn-group w-100" role="group">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleEdit(document)}
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  
                  {document.status === 'draft' && (
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => handlePublish(document.id)}
                      title="Publish"
                    >
                      <FaEye />
                    </button>
                  )}
                  
                  {document.status === 'published' && (
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleArchive(document.id)}
                      title="Archive"
                    >
                      <FaEyeSlash />
                    </button>
                  )}
                  
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleDelete(document.id)}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {documents.length === 0 && !loading && (
        <div className="text-center py-5">
          <h4 className="text-muted">No legal documents found</h4>
          <p className="text-muted">Create your first privacy policy or terms of service document.</p>
        </div>
      )}
    </div>
  );
};

export default LegalDocumentsManagement;
